import { useMemo, useState } from "react";
import { Building2, Check, Globe, Mail, Phone, Search, ShieldCheck, User } from "lucide-react";
import { industryIcon } from "./icons";
import Turnstile from "./Turnstile";

export const OTHER = "__other__";
export const MIN_CHALLENGE_CHARS = 100;

// -----------------------------
// CONTACT
// -----------------------------
const CONTACT_FIELDS = [
  { key: "companyName", label: "Company name", placeholder: "Acme Industries Pvt Ltd", icon: Building2, autoComplete: "organization" },
  { key: "name", label: "Your full name", placeholder: "Priya Sharma", icon: User, autoComplete: "name" },
  { key: "email", label: "Work email", placeholder: "priya@acme.com", icon: Mail, type: "email", autoComplete: "email" },
  { key: "mobile", label: "Mobile number", placeholder: "+91 98765 43210", icon: Phone, type: "tel", autoComplete: "tel" },
  { key: "website", label: "Company website", placeholder: "www.acme.com", icon: Globe, optional: true, full: true, autoComplete: "url" }
];

export function ContactStep({ company, onChange, errors, showErrors }) {
  const [touched, setTouched] = useState({});

  return (
    <div className="form-grid">
      {CONTACT_FIELDS.map(f => {
        const Icon = f.icon;
        const error = (showErrors || touched[f.key]) && errors[f.key];
        return (
          <div key={f.key} className={`field ${f.full ? "full" : ""}`}>
            <label htmlFor={f.key}>
              {f.label} {f.optional && <span className="opt">(optional)</span>}
            </label>
            <div className="input-wrap">
              <Icon size={17} />
              <input
                id={f.key}
                className={`input ${error ? "invalid" : ""}`}
                type={f.type || "text"}
                autoComplete={f.autoComplete}
                placeholder={f.placeholder}
                value={company[f.key] || ""}
                onChange={e => onChange({ [f.key]: e.target.value })}
                onBlur={() => setTouched(t => ({ ...t, [f.key]: true }))}
              />
            </div>
            {error && <p className="field-error">{error}</p>}
          </div>
        );
      })}
    </div>
  );
}

// -----------------------------
// INDUSTRY
// -----------------------------
export function IndustryStep({ industries, loading, company, onSelect, onChange, errors, showErrors }) {
  const [query, setQuery] = useState("");

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return industries;
    return industries.filter(i =>
      i.key === "other" ||
      i.label.toLowerCase().includes(q) ||
      (i.description || "").toLowerCase().includes(q)
    );
  }, [industries, query]);

  if (loading) return <p className="empty-note">Loading industries…</p>;

  return (
    <>
      <div className="search-box">
        <Search size={17} />
        <input
          className="input"
          placeholder="Search industries (e.g. hospital, logistics, SaaS)"
          value={query}
          onChange={e => setQuery(e.target.value)}
        />
      </div>

      <div className="industry-grid" role="radiogroup" aria-label="Industry">
        {filtered.map(ind => {
          const Icon = industryIcon(ind.icon);
          const selected = company.industry === ind.key;
          return (
            <button
              key={ind.key}
              type="button"
              role="radio"
              aria-checked={selected}
              className={`industry-card ${selected ? "selected" : ""}`}
              onClick={() => onSelect(ind)}
            >
              <span className="icon"><Icon size={19} /></span>
              <span>
                <span className="name">{ind.label}</span>
                <span className="desc" style={{ display: "block" }}>{ind.description}</span>
              </span>
              {selected && <span className="tick"><Check size={12} strokeWidth={3} /></span>}
            </button>
          );
        })}
      </div>

      {company.industry === "other" && (
        <div className="field" style={{ marginTop: 18 }}>
          <label htmlFor="industryOther">Which industry are you in?</label>
          <input
            id="industryOther"
            autoFocus
            className={`input ${showErrors && errors.industryOther ? "invalid" : ""}`}
            placeholder="e.g. Jewellery manufacturing, Security services"
            value={company.industryOther || ""}
            onChange={e => onChange({ industryOther: e.target.value })}
          />
          {showErrors && errors.industryOther && <p className="field-error">{errors.industryOther}</p>}
        </div>
      )}

      {showErrors && errors.industry && <p className="field-error">{errors.industry}</p>}
    </>
  );
}

// -----------------------------
// QUESTIONS
// -----------------------------
function QuestionCard({ q, number, value, otherText, onAnswer, onOther, error }) {
  const isMulti = q.type === "multi";
  const selected = isMulti ? (Array.isArray(value) ? value : []) : value;
  const options = q.has_other ? [...q.options, OTHER] : q.options;
  const otherActive = isMulti ? selected.includes(OTHER) : selected === OTHER;
  const answered = isMulti ? selected.length > 0 : Boolean(selected);

  const toggle = (opt) => {
    if (!isMulti) return onAnswer(opt);
    onAnswer(selected.includes(opt) ? selected.filter(o => o !== opt) : [...selected, opt]);
  };

  return (
    <div className={`question ${error ? "invalid" : answered ? "answered" : ""}`}>
      <div className="question-title">
        <span className="qnum">{number}</span>
        <div>
          <h3>{q.question}</h3>
          {(q.help || isMulti) && <div className="hint">{q.help || "Select all that apply"}</div>}
        </div>
      </div>

      <div className="options" role={isMulti ? "group" : "radiogroup"} aria-label={q.question}>
        {options.map(opt => {
          const isSel = isMulti ? selected.includes(opt) : selected === opt;
          return (
            <button
              type="button"
              key={opt}
              role={isMulti ? "checkbox" : "radio"}
              aria-checked={isSel}
              className={`option ${isMulti ? "multi" : ""} ${isSel ? "selected" : ""}`}
              onClick={() => toggle(opt)}
            >
              <span className="mark">{isSel && <Check size={11} strokeWidth={3.5} />}</span>
              {opt === OTHER ? "Other" : opt}
            </button>
          );
        })}
      </div>

      {otherActive && (
        <input
          className={`input other-input ${error && !otherText ? "invalid" : ""}`}
          placeholder="Please specify…"
          value={otherText || ""}
          autoFocus
          onChange={e => onOther(e.target.value)}
        />
      )}

      {error && <p className="field-error">{error}</p>}
    </div>
  );
}

export function QuestionsStep({ questions, startNumber, answers, otherInputs, onAnswer, onOther, errors, showErrors }) {
  return (
    <div className="question-list">
      {questions.map((q, i) => (
        <QuestionCard
          key={q.id}
          q={q}
          number={startNumber + i}
          value={answers[q.id]}
          otherText={otherInputs[q.id]}
          onAnswer={v => onAnswer(q.id, v)}
          onOther={v => onOther(q.id, v)}
          error={showErrors ? errors[q.id] : null}
        />
      ))}
    </div>
  );
}

// -----------------------------
// CHALLENGES + VERIFICATION
// -----------------------------
export function ChallengesStep({ company, onChange, errors, showErrors, turnstileKey, verified, onVerify, onExpire }) {
  const length = (company.challenges || "").trim().length;
  const enough = length >= MIN_CHALLENGE_CHARS;

  return (
    <>
      <div className="field">
        <label htmlFor="challenges">What are your biggest business challenges right now?</label>
        <textarea
          id="challenges"
          className={`textarea ${showErrors && errors.challenges ? "invalid" : ""}`}
          placeholder="e.g. Our team spends hours every day manually entering orders from email into our ERP, customer queries on WhatsApp go unanswered after hours, and we have no visibility into which products will run out of stock next month…"
          value={company.challenges || ""}
          onChange={e => onChange({ challenges: e.target.value })}
        />
        <p className="field-hint">
          <span>The more specific you are, the more tailored your report will be.</span>
          <span className={enough ? "ok" : ""}>
            {enough ? "✓ " : ""}{length}/{MIN_CHALLENGE_CHARS}
          </span>
        </p>
        {showErrors && errors.challenges && <p className="field-error">{errors.challenges}</p>}
      </div>

      <div className="verify-box">
        <span className="label">
          <ShieldCheck size={17} />
          {verified ? <span className="verified">Verified. You're good to go.</span> : "Quick security check"}
        </span>
        <div className="spacer" />
        <Turnstile key={turnstileKey} onVerify={onVerify} onExpire={onExpire} />
      </div>
      {showErrors && errors.turnstile && <p className="field-error">{errors.turnstile}</p>}
    </>
  );
}
